import React from "react";
import { cn } from "../../lib/utils";
import { Container, ContainerProps } from "./Container";
import { getSurfaceStyles } from "../../lib/surface";
import { getNavigation } from "../../lib/config";

export type SectionProps = {
  tone?: "neutral" | "brand" | "accent" | "contrast" | "muted";
  surface?: "solid" | "soft" | "transparent";
  padY?: "none" | "auto" | "sm" | "md" | "lg" | "xl";
  padX?: ContainerProps["padX"];
  max?: ContainerProps["max"];
  bleed?: boolean;
  fullWidth?: boolean;
  offsetForNav?: boolean;
  divider?: "none" | "top" | "bottom" | "both";
  rounded?: "none" | "md" | "lg" | "xl";
  shadow?: "none" | "sm" | "md";
  invert?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  id?: string;
};

export function Section({
  tone = "neutral",
  surface = "transparent",
  padY = "auto",
  padX,
  max,
  bleed = false,
  fullWidth = false,
  offsetForNav = false,
  divider = "none",
  rounded = "none",
  shadow = "none",
  invert,
  className,
  style: styleProp,
  children,
  id
}: SectionProps) {
  const { className: surfaceClasses, dataAttrs } = getSurfaceStyles({
    tone,
    surface,
    invert
  });
  const { navBarOffset } = getNavigation();
  const padYValue =
    padY === "auto"
      ? "var(--section-y)"
      : padY === "none"
        ? "0px"
        : `var(--space-${padY})`;
  const style: React.CSSProperties = {
    "--section-pad-y": padYValue,
    ...(offsetForNav
      ? {
        "--nav-bar-offset-mobile": navBarOffset.mobile,
        "--nav-bar-offset-desktop": navBarOffset.desktop
      }
      : {}),
    ...styleProp
  } as React.CSSProperties;
  const dividerClass =
    divider === "top"
      ? "border-t border-border"
      : divider === "bottom"
        ? "border-b border-border"
        : divider === "both"
          ? "border-y border-border"
          : "";
  const roundedClass =
    rounded === "md"
      ? "rounded-md"
      : rounded === "lg"
        ? "rounded-lg"
        : rounded === "xl"
          ? "rounded-xl"
          : "";
  const shadowClass =
    shadow === "sm" ? "shadow-sm" : shadow === "md" ? "shadow-md" : "";
  return (
    <section
      id={id}
      {...dataAttrs}
      style={style}
      className={cn(
        surfaceClasses,
        dividerClass,
        roundedClass,
        shadowClass,
        "py-[var(--section-pad-y)]",
        // Account for fixed navbar height on first section when requested
        offsetForNav &&
        "pt-[var(--nav-bar-offset-mobile)] md:pt-[var(--nav-bar-offset-desktop)] scroll-mt-[var(--nav-bar-offset-mobile)] md:scroll-mt-[var(--nav-bar-offset-desktop)]",
        fullWidth ? "w-full" : "max-w-screen-xl mx-auto",
        className
      )}
    >
      <Container bleed={bleed} padX={padX} max={max}>
        {children}
      </Container>
    </section>
  );
}
